import { customRussianMessages } from 'locales/ru/messages';
import { OrderAdjustmentsFormValues } from 'pages/OrderAdjustments/hooks/useOrderAdjustmentsForm';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { SelectInput } from 'shared/react-admin/Inputs';
import { getId } from 'utils';
import { toCamelCase } from 'utils/toCamelCase';

import { OrderAdjustmentsAdjustmentTypeSelect } from './OrderAdjustmentsAdjustmentTypeSelect';
import { StyledBox, StyledDialog } from './OrderAdjustmentsDialog.styled';

type Props = DialogProps & {
  onSubmit: VoidFunction;
};

type AdjustmentTypeMessagesKeys =
  keyof (typeof customRussianMessages)['app']['orderAdjustments']['adjustmentTypeMessages'];

export const OrderAdjustmentsDialog = ({ open, onClose, onSubmit }: Props) => {
  const translate = useTypedTranslate();
  const { watch, handleSubmit } = useFormContext<OrderAdjustmentsFormValues>();

  const [adjustmentType, impactToBalance] = watch(['adjustmentType', 'impactToBalance']);

  const submitHandler = () => {
    onClose();
    onSubmit();
  };

  const getAdjustmentTypeMessagesAlert = () => {
    if (!adjustmentType) {
      return '';
    }

    return translate(
      `app.orderAdjustments.adjustmentTypeMessages.${toCamelCase(getId(adjustmentType)) as AdjustmentTypeMessagesKeys}`,
    );
  };

  return (
    <StyledDialog onClose={onClose} open={open} width="640px">
      <form noValidate onSubmit={handleSubmit(submitHandler as any)}>
        <StyledBox>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.orderAdjustments.adjustmentDialogTitle')}
            titleSize="smallBold"
          />
          <CardContent>
            <OrderAdjustmentsAdjustmentTypeSelect />
            {adjustmentType && (
              <Box sx={{ mb: '22px' }}>
                <Alert severity="info">{getAdjustmentTypeMessagesAlert()}</Alert>
              </Box>
            )}
            <SelectInput
              choices={[
                { value: true, label: translate('app.orderAdjustments.balanceMovementTrue') },
                { value: false, label: translate('app.orderAdjustments.balanceMovementFalse') },
              ]}
              fullWidth
              label={translate('app.orderAdjustments.balanceMovementLabel')}
              optionText="label"
              optionValue="value"
              size="small"
              source="impactToBalance"
              validate={required()}
              variant="outlined"
            />
            {impactToBalance !== null && (
              <Box sx={{ mb: '22px' }}>
                <Alert severity="info">
                  {translate(
                    impactToBalance
                      ? 'app.orderAdjustments.impactToBalanceMessages.true'
                      : 'app.orderAdjustments.impactToBalanceMessages.false',
                  )}
                </Alert>
              </Box>
            )}
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
              <Button
                key="save-button"
                label={translate('app.orderAdjustments.submitButton')}
                type="submit"
                variant="contained"
              />,
            ]}
          />
        </StyledBox>
      </form>
    </StyledDialog>
  );
};
